/**
 * Beschriftung eines Belegungsbalkens. Den Gastnamen liefert der Server nur, wer Gastdaten sehen
 * darf; ohne dieses Recht heißt der Balken „Buchung“ (Board „Belegung · Eigentümer“), mit Recht
 * und ohne Namen „Ohne Gastnamen“ wie in der Buchungsliste.
 */
import type { BookingSpan } from './calendarHelpers';
import { dayLabel, daysInRange, parseDate } from './calendarDates';
import { occupancyShort } from './calendarRowModel';

const dayMonth = (iso: string) => {
  const date = parseDate(iso);
  return `${date.getUTCDate()}.${date.getUTCMonth() + 1}.`;
};

const guestOrBlank = (span: Pick<BookingSpan, 'guestName'>) => span.guestName || 'Ohne Gastnamen';

/** „Lea Hofmann · 2 Erw.“, für den Betrieb mit „· Plan 20.10.“, wenn eine Abreise geplant ist. */
export function barLabel(
  span: BookingSpan,
  { canSeeGuests, withPlan }: { canSeeGuests: boolean; withPlan: boolean },
): string {
  const plan =
    withPlan && span.earlyDeparturePlan
      ? ` · Plan ${dayMonth(span.earlyDeparturePlan.plannedDepartureDate)}${
          span.earlyDeparturePlan.status === 'NeedsAttention' ? ' – Klärung nötig' : ''
        }`
      : '';
  const name = canSeeGuests ? guestOrBlank(span) : 'Buchung';
  return `${name} · ${occupancyShort(span)}${plan}`;
}

/** Handy mit 7 Tagen: nur der Nachname, ohne Gastdaten-Recht „Buchung“ (Board „Belegung · Handy“). */
export function barShortLabel(span: BookingSpan, canSeeGuests: boolean): string {
  if (!canSeeGuests) return 'Buchung';
  return span.guestName?.trim().split(/\s+/).at(-1) || 'Ohne Gastnamen';
}

/** Zugänglicher Name: wer, von wann bis wann – der Balken selbst kürzt ab. */
export function barAria(span: BookingSpan, canSeeGuests: boolean): string {
  const departure = span.actualDepartureDate ?? span.checkOutDate;
  const name = canSeeGuests ? guestOrBlank(span) : `Buchung ${span.bookingReference}`;
  return `${name}, ${dayLabel(span.checkInDate)} bis ${dayLabel(departure)}, ${occupancyShort(span)}`;
}

/**
 * Spalte der geplanten vorzeitigen Abreise im sichtbaren Fenster (0-basiert) oder null. Die Marke
 * steht am Beginn des Abreisetags – dort endet die letzte Nacht.
 */
export function planColumn(span: BookingSpan, from: string, days: number): number | null {
  const planned = span.earlyDeparturePlan?.plannedDepartureDate;
  if (!planned) return null;
  const column = daysInRange(from, planned) - 1;
  return column > 0 && column < days ? column : null;
}
